// Modules
import { Link, useOutlet } from "react-router-dom";
import { useRoleManagement } from "containers/RoleManagement/useRoleManagement";

// Components
import { Button as ANTButton, Input } from "antd";
import getIcon, { IconProps } from "utils/getIcon";
import ExpandedRowTable from "components/atoms/ExpandedRowTable/ExpandedRowTable";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import { Head } from "components/molecules/Head/Head";
import CollapsibleTable from "components/atoms/CollapsibleTable/CollapsibleTable";
import Spinner from "components/atoms/Spinner/Spinner";

// styles
import styles from "containers/RoleManagement/roleManagementComponent.module.css";

// Constants
import { Routes, RoleManagement as Constants, PageTitles } from "constants/global";
import { ROLE_MANAGEMENT_COLUMN_DATA } from "constants/roleManagementData";

export type GroupDetails = {
	active: string;
	group_code: string;
	group_desc: string;
	group_id: any;
	permissions?: Array<string>;
};

const Icon = (props: IconProps) => getIcon({ ...props });

const RoleManagementPage = () => {
	const childRouteElement = useOutlet();
	const componentProps = useRoleManagement();

	const {
		handleExpandRows,
		getGroupHeader,
		generateCols,
		handleSearchGroup,
		handleKeyDown,
		handleUserSearch,
		updateEnterKeyEvent,
		currentExpandedGroup,
		updatedUserGroupList,
		searchGroup,
		searchUser,
		createUserAccess,
		createGroupAccess,
		loading,
		groupListLoading
	} = componentProps;

	return (
		<div>
			<Head>{PageTitles.ROLE_MANAGEMENT}</Head>
			{!childRouteElement && (
				<div className="relative">
					<div className={styles?.root}>
						{loading && <Spinner type="transparent" />}
						{groupListLoading && <Spinner type="full" />}
						<PageTitle showBackIcon={false} title={Constants.ROLE_HEADER} subTitle={Constants.ROLE_SUB_HEADER} />

						<div className={styles?.btn_wrapper}>
							<ANTButton className={createUserAccess ? styles?.btn1 : styles?.btn1_disabled} type="primary">
								<Link to={Routes?.CREATE_USER}>{Constants.CREATE_USER_LABEL}</Link>
							</ANTButton>
							<ANTButton className={createGroupAccess ? styles?.btn2 : styles?.btn2_disabled} type="primary">
								<Link to={Routes?.CREATE_GROUP}>{Constants.CREATE_GROUP_LABEL}</Link>
							</ANTButton>
						</div>

						<div className={styles?.txt}>{Constants.SEARCH_BY}</div>

						<div className={styles?.search_wrapper}>
							<Input
								placeholder={Constants.USERNAME_PLACEHOLDER}
								value={searchUser}
								onChange={handleUserSearch}
								onKeyDown={(event: any) => {
									if (event.key === "Enter") {
										updateEnterKeyEvent();
									}
								}}
								className={styles?.searchUserInput}
								suffix={
									<Icon
										height="24px"
										width="24px"
										icon="search"
										onClickHandler={() => {
											updateEnterKeyEvent();
										}}
									/>
								}
							/>
							<div className={styles?.ortxt}>or</div>
							<Input
								placeholder={Constants.USERGROUP_PLACEHOLDER}
								value={searchGroup}
								onChange={handleSearchGroup}
								onKeyDown={(event: any) => {
									if (event.key === "Enter") {
										handleKeyDown();
									}
								}}
								className={styles?.searchGroupInput}
								suffix={
									<Icon
										height="24px"
										width="24px"
										icon="search"
										onClickHandler={() => {
											handleKeyDown();
										}}
									/>
								}
							/>
						</div>
					</div>
					{updatedUserGroupList &&
						updatedUserGroupList?.map((groupItem) => {
							return (
								<div className={styles.table_container} key={groupItem.group_code}>
									<CollapsibleTable
										defaultExpandedGroupKey={Constants.DEFAULT_EXPANDED_ROW_KEY}
										currentExpandedGroup={currentExpandedGroup}
										handleExpandRows={handleExpandRows}
										keyIndex={groupItem.group_code}
										key={groupItem.group_id}
										rowClassName="collapsible-table"
										showHeader={false}
										generateCols={generateCols}
										groupItem={[groupItem]}
										generateData={getGroupHeader}
										expandedRowElement={() => (
											<ExpandedRowTable
												searchUser={searchUser}
												columnHeaderData={ROLE_MANAGEMENT_COLUMN_DATA}
												groupDetails={groupItem}
												rowsPerPage={Constants.DEFAULT_ROW_PER_PAGE}
											/>
										)}
									/>
								</div>
							);
						})}
					{searchGroup && updatedUserGroupList?.length === 0 && <p className={styles?.not_found_error}>{Constants.USER_GROUP_NOT_FOUND}</p>}
				</div>
			)}
			{childRouteElement}
		</div>
	);
};

export default RoleManagementPage;
